import { t } from "@/i18n-js/instance";
import { useUpgradePlanModal } from "@/react/components/shared/UpgradePlan";
import { SCHEDULED_DOWNGRADE_TYPES } from "@/react/components/shared/UpgradePlan/constants";
import { CANCEL_SCHEDULED_DOWNGRADE_SOURCES } from "@/react/components/shared/UpgradePlan/types";
import { getI18nBillingCycle } from "@/react/helpers/plansHelpers";
import type { CurrentPlan } from "@/react/types/CurrentPlan";
import { Icon } from "@circle-react/components/shared/Icon";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Button } from "@circle-react-uikit/Button";
import { WarningWrapper } from "./WarningWrapper";

interface ScheduledSubscriptionBannerProps {
  currentPlan: CurrentPlan;
}

export const ScheduledSubscriptionBanner = ({
  currentPlan,
}: ScheduledSubscriptionBannerProps) => {
  const scheduledDowngradeMetadata = currentPlan?.scheduled_downgrade_metadata;
  const isPlanDowngrade = scheduledDowngradeMetadata?.plan_downgrade;
  const scheduledDowngradeType = isPlanDowngrade
    ? SCHEDULED_DOWNGRADE_TYPES.PLAN_DOWNGRADE
    : SCHEDULED_DOWNGRADE_TYPES.INTERVAL_DOWNGRADE;
  const i18nRoot = `pricing_plans.downgrade_schedule_banner.${scheduledDowngradeType}`;

  const upgradeModal = useUpgradePlanModal();

  if (!scheduledDowngradeMetadata?.next_plan) return null;

  const nextPlanName = getI18nBillingCycle(
    scheduledDowngradeMetadata.next_plan,
  ).toLowerCase();

  const switchDate = scheduledDowngradeMetadata.switch_date;
  const discountAmount =
    scheduledDowngradeMetadata.annual_discount_amount_with_currency;

  const handleBannerAction = () => {
    void upgradeModal.show({
      source: CANCEL_SCHEDULED_DOWNGRADE_SOURCES.SCHEDULED_DOWNGRADE_BANNER,
      showPlanDetails: false,
    });
  };

  return (
    <WarningWrapper>
      <div className="flex items-start gap-4">
        <div className="mr-2 mt-[2px]">
          <Icon type="information-outline" size={24} />
        </div>
        <div className="flex-1">
          <div className="mb-1">
            <Typography.LabelMd color="text-default" weight="semibold">
              {t([i18nRoot, "title"], {
                nextPlanName,
                switchDate,
              })}
            </Typography.LabelMd>
          </div>
          <div className="mb-3">
            <Typography.BodySm color="text-default">
              {t([i18nRoot, "body"], {
                discountAmount,
              })}
            </Typography.BodySm>
          </div>
          <Button
            variant="secondary"
            className="border-secondary hover:bg-secondary rounded-full border bg-white px-4 py-2 text-sm font-semibold text-black shadow-sm"
            onClick={handleBannerAction}
          >
            {t([i18nRoot, "cta_button"])}
          </Button>
        </div>
      </div>
    </WarningWrapper>
  );
};
